import { useEffect, useMemo, useRef, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./ReadingInhibition.css";

const NUMBER_WORDS = [
  { value: 0, word: "cero" },
  { value: 1, word: "uno" },
  { value: 2, word: "dos" },
  { value: 3, word: "tres" },
  { value: 4, word: "cuatro" },
  { value: 5, word: "cinco" },
  { value: 6, word: "seis" },
  { value: 7, word: "siete" },
  { value: 8, word: "ocho" },
  { value: 9, word: "nueve" },
  { value: 10, word: "diez" },
  { value: 11, word: "once" },
  { value: 12, word: "doce" },
  { value: 13, word: "trece" },
  { value: 14, word: "catorce" },
  { value: 15, word: "quince" },
  { value: 16, word: "dieciséis" },
  { value: 17, word: "diecisiete" },
  { value: 18, word: "dieciocho" },
  { value: 19, word: "diecinueve" },
  { value: 20, word: "veinte" },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 3,
    minValue: 1,
    maxValue: 9,
    responseTime: 6000,
    congruentProbability: 0.4,
  },

  medium: {
    label: "Media",
    totalRounds: 12,
    itemCount: 4,
    minValue: 0,
    maxValue: 12,
    responseTime: 4500,
    congruentProbability: 0.2,
  },

  hard: {
    label: "Difícil",
    totalRounds: 16,
    itemCount: 5,
    minValue: 0,
    maxValue: 20,
    responseTime: 3000,
    congruentProbability: 0,
  },
};

const getRandomInteger = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const getAvailableNumbers = (minValue, maxValue) =>
  NUMBER_WORDS.filter(
    ({ value }) =>
      value >= minValue && value <= maxValue
  );

const createRound = ({
  itemCount,
  minValue,
  maxValue,
  congruentProbability,
}) => {
  const availableNumbers = getAvailableNumbers(
    minValue,
    maxValue
  );

  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 3, 2),
    availableNumbers.length
  );

  const displayedNumber =
    availableNumbers[
      getRandomInteger(0, availableNumbers.length - 1)
    ];

  const isCongruent =
    Math.random() < Number(congruentProbability);

  let writtenNumber = displayedNumber;

  if (!isCongruent) {
    const incongruentCandidates =
      availableNumbers.filter(
        ({ value }) =>
          value !== displayedNumber.value
      );

    writtenNumber =
      incongruentCandidates[
        getRandomInteger(
          0,
          incongruentCandidates.length - 1
        )
      ];
  }

  const distractors = shuffleArray(
    availableNumbers.filter(
      ({ value }) =>
        value !== displayedNumber.value
    )
  )
    .slice(0, safeItemCount - 1)
    .map(({ value }) => value);

  const options = shuffleArray([
    displayedNumber.value,
    ...distractors,
  ]);

  return {
    id: `${Date.now()}-${Math.random()}`,
    displayedNumber: displayedNumber.value,
    writtenWord: writtenNumber.word,
    writtenNumber: writtenNumber.value,
    options,
    isCongruent:
      displayedNumber.value === writtenNumber.value,
  };
};

function ReadingInhibition() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);
  const [omissions, setOmissions] = useState(0);

  const [
    congruentCorrectAnswers,
    setCongruentCorrectAnswers,
  ] = useState(0);

  const [
    incongruentCorrectAnswers,
    setIncongruentCorrectAnswers,
  ] = useState(0);

  const [congruentRounds, setCongruentRounds] =
    useState(0);

  const [incongruentRounds, setIncongruentRounds] =
    useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const [timeRemaining, setTimeRemaining] =
    useState(0);

  const responseLockedRef = useRef(false);
  const roundTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const totalResponses =
    correctAnswers + errors + omissions;

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalResponses
  );

  const totalErrors = calculateErrorCount(
    errors,
    omissions
  );

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const clearRoundTimers = () => {
    if (roundTimeoutRef.current) {
      clearTimeout(roundTimeoutRef.current);
      roundTimeoutRef.current = null;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const generateRound = (nextConfig) =>
    createRound({
      itemCount: Number(nextConfig.itemCount),
      minValue: Number(nextConfig.minValue),
      maxValue: Number(nextConfig.maxValue),
      congruentProbability: Number(
        nextConfig.congruentProbability
      ),
    });

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    clearRoundTimers();

    const nextConfig = {
      ...resolvedConfig,
      difficultyKey,
      totalRounds:
        Number(resolvedConfig.totalRounds) || 8,
      itemCount:
        Number(resolvedConfig.itemCount) || 3,
      minValue:
        Number(resolvedConfig.minValue) || 0,
      maxValue:
        Number(resolvedConfig.maxValue) || 9,
      responseTime:
        Number(resolvedConfig.responseTime) ||
        5000,
      congruentProbability:
        resolvedConfig.congruentProbability !==
        undefined
          ? Number(
              resolvedConfig.congruentProbability
            )
          : 0.2,
    };

    if (
      nextConfig.maxValue <= nextConfig.minValue
    ) {
      nextConfig.maxValue =
        nextConfig.minValue + 5;
    }

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(generateRound(nextConfig));

    setStarted(true);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setOmissions(0);

    setCongruentCorrectAnswers(0);
    setIncongruentCorrectAnswers(0);

    setCongruentRounds(0);
    setIncongruentRounds(0);

    setResponses([]);

    const now = Date.now();

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);

    setTimeRemaining(
      nextConfig.responseTime
    );

    responseLockedRef.current = false;
  };

  const finishGame = () => {
    clearRoundTimers();

    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const moveToNextRound = () => {
    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = generateRound(config);
    const now = Date.now();

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);
    setRoundStartTime(now);
    setTimeRemaining(config.responseTime);

    responseLockedRef.current = false;
  };

  const registerRoundType = (currentRound) => {
    if (currentRound.isCongruent) {
      setCongruentRounds(
        (current) => current + 1
      );
    } else {
      setIncongruentRounds(
        (current) => current + 1
      );
    }
  };

  const handleAnswer = (selectedNumber) => {
    if (
      !round ||
      responseLockedRef.current
    ) {
      return;
    }

    responseLockedRef.current = true;
    clearRoundTimers();

    const responseTime =
      Date.now() - roundStartTime;

    const isCorrect =
      selectedNumber === round.displayedNumber;

    registerRoundType(round);

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    if (isCorrect) {
      setCorrectAnswers(
        (current) => current + 1
      );

      if (round.isCongruent) {
        setCongruentCorrectAnswers(
          (current) => current + 1
        );
      } else {
        setIncongruentCorrectAnswers(
          (current) => current + 1
        );
      }
    } else {
      setErrors((current) => current + 1);
    }

    moveToNextRound();
  };

  const handleOmission = () => {
    if (
      !round ||
      responseLockedRef.current
    ) {
      return;
    }

    responseLockedRef.current = true;
    clearRoundTimers();

    registerRoundType(round);

    setOmissions((current) => current + 1);

    moveToNextRound();
  };

  useEffect(() => {
    if (
      !started ||
      finished ||
      !round ||
      !config
    ) {
      return undefined;
    }

    clearRoundTimers();

    const responseTime = Number(
      config.responseTime
    );

    setTimeRemaining(responseTime);

    roundTimeoutRef.current = setTimeout(
      handleOmission,
      responseTime
    );

    countdownIntervalRef.current =
      setInterval(() => {
        setTimeRemaining((current) =>
          Math.max(current - 100, 0)
        );
      }, 100);

    return () => {
      clearRoundTimers();
    };
  }, [round?.id, started, finished]);

  useEffect(
    () => () => {
      clearRoundTimers();
    },
    []
  );

  const timePercentage = config
    ? Math.max(
        0,
        Math.min(
          100,
          (timeRemaining /
            config.responseTime) *
            100
        )
      )
    : 0;

  const congruentAccuracy =
    calculateAccuracy(
      congruentCorrectAnswers,
      congruentRounds
    );

  const incongruentAccuracy =
    calculateAccuracy(
      incongruentCorrectAnswers,
      incongruentRounds
    );

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Inhibición de la lectura"
        description="Observa la cifra e ignora la palabra escrita. Pulsa el número que aparece como cifra lo más rápido posible."
        areas={[
          "Control inhibitorio",
          "Atención selectiva",
          "Velocidad de procesamiento",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 12,
          }),

          GameFields.itemCount({
            label: "Opciones de respuesta",
            min: 2,
            max: 6,
            defaultValue: 4,
          }),

          GameFields.responseTime({
            label:
              "Tiempo máximo por estímulo",
            min: 2000,
            max: 10000,
            step: 500,
            defaultValue: 4500,
          }),

          {
            key: "minValue",
            type: "number",
            label: "Número mínimo",
            min: 0,
            max: 15,
            defaultValue: 0,
          },

          {
            key: "maxValue",
            type: "number",
            label: "Número máximo",
            min: 4,
            max: 20,
            defaultValue: 12,
          },

          {
            key: "congruentProbability",
            type: "number",
            label:
              "Proporción de estímulos congruentes",
            min: 0,
            max: 1,
            step: 0.1,
            defaultValue: 0.2,
          },
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="reading-inhibition-page">
        <div className="reading-inhibition-results">
          <GameResults
            title="Resultados"
            correctAnswers={correctAnswers}
            totalAnswers={totalResponses}
            accuracy={accuracy}
            errors={errors}
            omissions={omissions}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="reading-inhibition-results-grid">
            <div className="reading-inhibition-result-card">
              <span>Aciertos</span>
              <strong>{correctAnswers}</strong>
            </div>

            <div className="reading-inhibition-result-card">
              <span>Errores de comisión</span>
              <strong>{errors}</strong>
            </div>

            <div className="reading-inhibition-result-card">
              <span>Omisiones</span>
              <strong>{omissions}</strong>
            </div>

            <div className="reading-inhibition-result-card">
              <span>
                Precisión incongruente
              </span>

              <strong>
                {Math.round(
                  incongruentAccuracy
                )}
                %
              </strong>
            </div>
          </div>

          <div className="reading-inhibition-comparison">
            <div>
              <span>Estímulos congruentes</span>

              <strong>
                {congruentCorrectAnswers}/
                {congruentRounds}
              </strong>

              <small>
                {Math.round(congruentAccuracy)}%
                de precisión
              </small>
            </div>

            <div>
              <span>
                Estímulos incongruentes
              </span>

              <strong>
                {incongruentCorrectAnswers}/
                {incongruentRounds}
              </strong>

              <small>
                {Math.round(
                  incongruentAccuracy
                )}
                % de precisión
              </small>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="reading-inhibition-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reading-inhibition-page">
      <main className="reading-inhibition-card">
        <header className="reading-inhibition-header">
          <div>
            <p className="reading-inhibition-area">
              Control inhibitorio
            </p>

            <h1>Inhibición de la lectura</h1>

            <p>
              Responde a la cifra e ignora la
              palabra.
            </p>
          </div>

          <div className="reading-inhibition-score">
            <span>Aciertos</span>
            <strong>{correctAnswers}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <div className="reading-inhibition-timer">
          <div
            className="reading-inhibition-timer-fill"
            style={{
              width: `${timePercentage}%`,
            }}
          />
        </div>

        <section className="reading-inhibition-game">
          <p className="reading-inhibition-instruction">
            ¿Qué <strong>cifra</strong> aparece?
          </p>

          <div className="reading-inhibition-stimulus">
            <span className="reading-inhibition-word">
              {round?.writtenWord}
            </span>

            <strong className="reading-inhibition-number">
              {round?.displayedNumber}
            </strong>
          </div>

          <p className="reading-inhibition-reminder">
            Ignora la palabra y pulsa la cifra.
          </p>

          <div className="reading-inhibition-options">
            {round?.options.map(
              (option, index) => (
                <button
                  type="button"
                  key={`${round.id}-${option}-${index}`}
                  className="reading-inhibition-option"
                  onClick={() =>
                    handleAnswer(option)
                  }
                >
                  {option}
                </button>
              )
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default ReadingInhibition;